/**
 * SoftphoneContext — Wraps the Twilio Voice SDK Device.
 *
 * Exposes:
 *   • status: "offline" | "connecting" | "ready" | "busy" | "error"
 *   • error: string | null (last-error surface, cleared on retry)
 *   • activeCall: { direction, from, to, startedAt } | null
 *   • incoming: pending Twilio.Call | null (from device.on('incoming'))
 *   • goOnline / goOffline / dial(to, leadId?) / hangup / mute / sendDtmf / acceptIncoming / rejectIncoming
 *
 * Silent token refresh: whenever the SDK emits 'tokenWillExpire', we hit
 * POST /api/bdc/phone/token and hand the fresh JWT to Device.updateToken().
 *
 * We only mount the Device when the user manually goes online (per PM: saves
 * browser resources + gives BDC agent explicit control over availability).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Device } from "@twilio/voice-sdk";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const SoftphoneContext = createContext(null);

const STATUS = {
  OFFLINE: "offline",
  CONNECTING: "connecting",
  READY: "ready",
  BUSY: "busy",
  ERROR: "error",
};

const buildActiveCall = (call, direction) => ({
  direction,
  from: call?.parameters?.From || call?.customParameters?.get?.("From") || null,
  to: call?.parameters?.To || call?.customParameters?.get?.("To") || null,
  startedAt: new Date().toISOString(),
  sid: call?.parameters?.CallSid || null,
});

export const SoftphoneProvider = ({ children }) => {
  const [status, setStatus] = useState(STATUS.OFFLINE);
  const [error, setError] = useState(null);
  const [activeCall, setActiveCall] = useState(null); // in-progress call
  const [incoming, setIncoming] = useState(null); // pending Call obj
  const [muted, setMuted] = useState(false);
  const [twilioNumber, setTwilioNumber] = useState(null);

  const deviceRef = useRef(null);
  const currentCallRef = useRef(null);
  const registeredRef = useRef(false);

  const cleanupDevice = useCallback(async () => {
    const d = deviceRef.current;
    if (!d) return;
    try {
      d.removeAllListeners?.();
      if (registeredRef.current) {
        await d.unregister();
      }
      d.destroy();
    } catch (e) {
      // no-op — device may already be disposed
    }
    deviceRef.current = null;
    registeredRef.current = false;
  }, []);

  const fetchToken = useCallback(async () => {
    const { data } = await api.post("/bdc/phone/token");
    if (!data?.token) throw new Error("No token returned");
    if (data.twilio_number) setTwilioNumber(data.twilio_number);
    return data.token;
  }, []);

  const goOnline = useCallback(async () => {
    if (status === STATUS.CONNECTING || status === STATUS.READY) return;
    setStatus(STATUS.CONNECTING);
    setError(null);
    // Sanitized user-facing copy — Phase 11: never surface the raw Twilio
    // SDK error string (it sometimes mentions "authentication" / "identity"
    // which confused a BDC agent into thinking they had to sign into Twilio.
    // Backend uses server-minted access tokens; the user never talks to
    // Twilio directly.)
    let friendlyError = null;
    try {
      const token = await fetchToken();
      friendlyError = "Softphone couldn't start. Check microphone permission or try again.";
      const device = new Device(token, {
        codecPreferences: ["opus", "pcmu"],
        edge: "ashburn",
        logLevel: "warn",
        // Enable keepalive so the Device auto-reconnects on transient net drops
        allowIncomingWhileBusy: false,
      });
      deviceRef.current = device;

      device.on("registered", () => {
        registeredRef.current = true;
        setStatus(STATUS.READY);
      });
      device.on("unregistered", () => {
        registeredRef.current = false;
        setStatus(STATUS.OFFLINE);
      });
      device.on("error", (err) => {
        // eslint-disable-next-line no-console
        console.error("[Softphone] device error", err);
        setError("Softphone connection lost. Click Retry to reconnect.");
        setStatus(STATUS.ERROR);
      });
      device.on("tokenWillExpire", async () => {
        try {
          const fresh = await fetchToken();
          await device.updateToken(fresh);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[Softphone] token refresh failed", e);
          setError("Softphone session expired. Click Retry to reconnect.");
          setStatus(STATUS.ERROR);
          await cleanupDevice();
        }
      });
      device.on("incoming", (call) => {
        setIncoming(call);
        // Auto-cleanup incoming state if caller cancels before pickup
        call.on("cancel", () => {
          if (currentCallRef.current === call) currentCallRef.current = null;
          setIncoming((cur) => (cur === call ? null : cur));
        });
        call.on("disconnect", () => {
          if (currentCallRef.current === call) {
            currentCallRef.current = null;
            setActiveCall(null);
            setMuted(false);
            setStatus(STATUS.READY);
          }
          setIncoming((cur) => (cur === call ? null : cur));
        });
      });

      friendlyError = "Softphone failed to register. Please contact support.";
      await device.register();
      // Notify the backend we are online + available.
      try {
        await api.post("/bdc/phone/presence", { is_available: true });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[Softphone] presence update failed", formatApiError(e));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[Softphone] goOnline failed (raw):", err, "message:", err?.message);
      setError(friendlyError || "Softphone couldn't start. Click Retry to try again.");
      setStatus(STATUS.ERROR);
      toast.error("Softphone couldn't connect. Click Retry to try again.");
      await cleanupDevice();
    }
  }, [status, fetchToken, cleanupDevice]);

  const retry = useCallback(async () => {
    // Full reset then re-attempt goOnline.
    await cleanupDevice();
    setError(null);
    setStatus(STATUS.OFFLINE);
    // slight defer so React commits the state change before we re-enter
    setTimeout(() => { goOnline(); }, 50);
  }, [cleanupDevice, goOnline]);

  const goOffline = useCallback(async () => {
    // Hang up any active/incoming call cleanly first
    try {
      currentCallRef.current?.disconnect();
      incoming?.reject?.();
    } catch (_e) {}
    try {
      await api.post("/bdc/phone/offline");
    } catch (_e) {}
    await cleanupDevice();
    currentCallRef.current = null;
    setActiveCall(null);
    setIncoming(null);
    setMuted(false);
    setStatus(STATUS.OFFLINE);
  }, [cleanupDevice, incoming]);

  const attachCallHandlers = useCallback((call, direction) => {
    call.on("accept", () => {
      currentCallRef.current = call;
      setActiveCall(buildActiveCall(call, direction));
      setStatus(STATUS.BUSY);
    });
    call.on("mute", (isMuted) => {
      setMuted(!!isMuted);
    });
    call.on("disconnect", () => {
      currentCallRef.current = null;
      setActiveCall(null);
      setMuted(false);
      setStatus(STATUS.READY);
    });
    call.on("cancel", () => {
      currentCallRef.current = null;
      setActiveCall(null);
      setMuted(false);
      setStatus(STATUS.READY);
    });
    call.on("error", (err) => {
      console.error("[Softphone] call error", err);
      toast.error(err?.message || "Call failed");
    });
  }, []);

  const dial = useCallback(
    async (toPhone, leadId = null) => {
      if (!deviceRef.current || status !== STATUS.READY) {
        toast.error("Softphone is not ready. Go online first.");
        return;
      }
      const cleanedTo = String(toPhone || "").trim();
      if (!cleanedTo) return;
      // Best-effort — log the intent server-side for lead attribution
      try {
        await api.post("/bdc/phone/dial", { to_phone: cleanedTo, lead_id: leadId });
      } catch (_e) {}
      try {
        const params = { To: cleanedTo };
        if (leadId) params.LeadId = leadId;
        const call = await deviceRef.current.connect({ params });
        attachCallHandlers(call, "outbound");
        currentCallRef.current = call;
        setActiveCall({
          direction: "outbound",
          from: twilioNumber,
          to: cleanedTo,
          startedAt: new Date().toISOString(),
          sid: null,
        });
        setStatus(STATUS.BUSY);
      } catch (err) {
        toast.error(err?.message || "Could not place the call");
      }
    },
    [status, attachCallHandlers, twilioNumber]
  );

  const acceptIncoming = useCallback(() => {
    if (!incoming) return;
    attachCallHandlers(incoming, "inbound");
    incoming.accept();
    currentCallRef.current = incoming;
    setActiveCall(buildActiveCall(incoming, "inbound"));
    setIncoming(null);
    setStatus(STATUS.BUSY);
  }, [incoming, attachCallHandlers]);

  const rejectIncoming = useCallback(() => {
    if (!incoming) return;
    try {
      incoming.reject();
    } catch (_e) {}
    setIncoming(null);
  }, [incoming]);

  const hangup = useCallback(() => {
    try {
      currentCallRef.current?.disconnect();
    } catch (_e) {}
  }, []);

  const toggleMute = useCallback(() => {
    const call = currentCallRef.current;
    if (!call) return;
    try {
      const next = !call.isMuted();
      call.mute(next);
      setMuted(next);
    } catch (_e) {}
  }, []);

  const sendDtmf = useCallback((digit) => {
    const call = currentCallRef.current;
    if (!call) return;
    try {
      call.sendDigits(String(digit));
    } catch (_e) {}
  }, []);

  useEffect(() => {
    return () => {
      cleanupDevice();
    };
  }, [cleanupDevice]);

  const value = {
    status,
    error,
    activeCall,
    incoming,
    muted,
    twilioNumber,
    goOnline,
    goOffline,
    retry,
    dial,
    hangup,
    toggleMute,
    sendDtmf,
    acceptIncoming,
    rejectIncoming,
  };

  return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
};

export const useSoftphone = () => {
  const ctx = useContext(SoftphoneContext);
  if (!ctx) throw new Error("useSoftphone must be used inside <SoftphoneProvider>");
  return ctx;
};

export const SOFTPHONE_STATUS = STATUS;
